import { Inject, Injectable, PLATFORM_ID, Signal, WritableSignal, computed, inject, signal } from "@angular/core";
import { LocalStorageService } from "../local-storage.service";
import { AttributionService } from "../attribution/attribution.service";
import { LOCALSTORAGE_KEYS } from "../constants/databases";
import { Session } from "../types/session.type";
import { HttpClient, HttpHeaders } from "@angular/common/http";
import { environment } from "../../../environments/environment";
import { tap } from "rxjs";
import { isPlatformBrowser } from "@angular/common";

declare global {
  interface Window {
    klaviyo: any;
  }
}

@Injectable({ providedIn: 'root' })
export class KlaviyoService {
  #localStorageService = inject(LocalStorageService);
  #attributionService = inject(AttributionService);
  #http = inject(HttpClient);

  #klaviyoLoaded: WritableSignal<boolean> = signal(false);
  getKlaviyoLoaded: Signal<boolean> = computed(() => this.#klaviyoLoaded());
  #shouldLoadKlaviyo: WritableSignal<boolean> = signal(environment.apis.klaviyo.publicApiKey !== '');

  #authCallback?: () => void;

  constructor(@Inject(PLATFORM_ID) private platformId: any) {
    if (!this.#shouldLoadKlaviyo() || !isPlatformBrowser(this.platformId)) return; // Check if Klaviyo should be loaded based on the environment variable
    this.#waitForKlaviyoObject(); // Wait for the Klaviyo script to load
    setTimeout(() => this.#loadKlaviyoScript(), 1000); // Load the Klaviyo script when the service is instantiated
  }

  #loadKlaviyoScript() {
    if (!isPlatformBrowser(this.platformId)) return; // Ensure this runs only in the browser
    if (document.getElementById('klaviyo-script'))
      return; // If the script is already loaded, do nothing

    const script = document.createElement('script');
    script.id = 'klaviyo-script';
    script.src = `https://static.klaviyo.com/onsite/js/klaviyo.js?company_id=${environment.apis.klaviyo.publicApiKey}`;
    script.async = true;

    document.body.appendChild(script);
  }

  trackEvent(event: string, properties: any) {

    const customerData: Session | null = this.#localStorageService.get(LOCALSTORAGE_KEYS.SESSION) || null;
    const hasSession = !!customerData?.accountInfo?.id
    if (!hasSession) return;

    const companyId = environment.apis.klaviyo.publicApiKey;
    if (!companyId || !environment.production) return;

    const headers = new HttpHeaders({
      accept: 'application/json',
      revision: '2024-07-15',
      'ContentType': 'application/json',
    });

    const url = `https://a.klaviyo.com/client/events/?company_id=${companyId}`;

    try {
      this.#http.post(url,
        {
          data: {
            type: 'event',
            attributes: {
              properties,
              metric: { data: { type: 'metric', attributes: { name: event } } },
              profile: {
                data: {
                  type: 'profile',
                  attributes: {
                    email: customerData.accountInfo.email,
                    phone_number: customerData.accountInfo.phone,
                    first_name: customerData.accountInfo.firstName,
                    last_name: customerData.accountInfo.lastName,
                    location: {
                      address1: customerData.address?.street || '',
                      address2: customerData.address?.additional || '',
                      city: customerData.address?.city || '',
                      zip: customerData.address?.zip || '',
                    }
                  }
                }
              }
            }
          }
        }, { headers }).pipe(
          tap((res: any) => {
            // console.log(res);
          })
        ).subscribe();
    } catch (error) {
      console.log('Error en trackEvent: ', error);
    }
  }

  #waitForKlaviyoObject() {
    const checkKlaviyo = () => {
      if (window.klaviyo && typeof window.klaviyo.identify === "function") {
        this.#klaviyoLoaded.set(true);
        clearInterval(checkInterval);

        if (this.#authCallback) {
          this.#authCallback();
        }
      }
    }

    const checkInterval = setInterval(() => checkKlaviyo(), 50); // Verify every 50ms
    checkKlaviyo(); // Initial check
  }

  async identiyUser(): Promise<boolean> {
    return new Promise(async (resolve) => {
      const customer: Session | null = this.#localStorageService.get(LOCALSTORAGE_KEYS.SESSION) || null;
      const hasSession = !!customer?.accountInfo?.id
      if (!hasSession) return resolve(false);

      if (this.#klaviyoLoaded() && (window as any).klaviyo) {
        if (window.klaviyo.isIdentified().__zone_symbol__value !== true) {
          window.klaviyo.identify({
            "email": customer.accountInfo.email,
            "phone_number": customer.accountInfo.phone,
            "first_name": customer.accountInfo.firstName,
            "last_name": customer.accountInfo.lastName,
            // Where the visitor came from (DEV-10930). The onsite identify call takes
            // arbitrary custom profile properties, and this brand has no earlier
            // profile-creating step to hang them off — the email first reaches Klaviyo
            // here. The spread is a no-op for direct, untagged traffic.
            ...this.#attributionService.getProperties()
          });

          setTimeout(async () => {
            if (window.klaviyo.isIdentified().__zone_symbol__value) {
              return resolve(true);
            }
            return resolve(false);
          }, 1000);

        } else {
          return resolve(true); // User is already authenticated
        }

      } else {
        this.#authCallback = () => this.identiyUser(); // Save the callback to be called later
        return resolve(false); // Return false to indicate that the user is not authenticated yet
      }

    })
  }

}
